/*
 * Copyright (C) 2026 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import {captureException} from '@sentry/react'
import type {GlobalEnv} from './GlobalEnv'

type EnvWithFeatures = {FEATURES?: GlobalEnv['FEATURES'] | null}
type FeatureName = keyof NonNullable<GlobalEnv['FEATURES']>

let reportedMissingFeatures = false

/**
 * Read a boolean-ish ENV feature flag, failing closed when FEATURES is missing
 * (some mobile/wiki loads omit FEATURES entirely). Report once per page load so
 * the underlying delivery bug stays visible without spamming Sentry.
 */
export function featureFlagOn(
  name: FeatureName,
  env: EnvWithFeatures | undefined | null = typeof window !== 'undefined' ? window.ENV : undefined,
): boolean {
  if (env?.FEATURES == null) {
    if (!reportedMissingFeatures) {
      reportedMissingFeatures = true
      captureException(new Error('ENV.FEATURES is missing; feature flags fail closed'))
    }
    return false
  }
  return Boolean(env.FEATURES[name])
}

/** Test-only: reset the once-per-page missing-FEATURES reporter. */
export function __resetMissingFeaturesReportForTests() {
  reportedMissingFeatures = false
}
